/**
 * ClientsPage.js - Gestão de Clientes
 * CG Business ERP - P0.3 Extended with Pending Actions
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ModulePageHero } from '@/components/common/ModulePageHero';
import { ResizableTableHandle } from '@/components/common/ResizableTableHandle';
import { toast } from 'sonner';
import { 
  Users, Plus, Edit, Trash2, Search, Building, User, Phone, Mail, 
  MapPin, FileText, RotateCcw, History, Eye, AlertTriangle, Key, Shield, 
  Clock, MessageSquare, Repeat, ShoppingCart, Wrench, Upload, Download, CheckSquare
} from 'lucide-react';
import { formatDateTime, formatCurrency, formatDisplayCode, cn } from '@/lib/utils';
import { clientsAPI, licensesAPI, getErrorMessage } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useResizableTableColumns } from '@/hooks/useResizableTableColumns';
import WhatsAppButton from '@/components/WhatsAppButton';
import { UniversalCrudActions } from '@/components/UniversalCrudActions';
import { useLanguage } from '@/i18n';

const CLIENT_TYPES = [
  { value: 'individual', label: 'Particular', icon: User },
  { value: 'company', label: 'Empresa', icon: Building },
];

const CLIENTS_TABLE_COLUMN_WIDTHS = {
  selector: 52,
  code: 118,
  name: 220,
  type: 110,
  nuit: 136,
  phone: 150,
  email: 220,
  city: 144,
  actions: 140,
};

export default function ClientsPage() {
  const { t } = useLanguage();
  const { hasRole } = useAuth();
  const { getColumnStyle: getClientsColumnStyle, startColumnResize: startClientsColumnResize } = useResizableTableColumns({
    storageKey: 'cg-clients-list-columns',
    defaultWidths: CLIENTS_TABLE_COLUMN_WIDTHS,
    minWidth: 72,
  });
  const isAdmin = hasRole(['admin']);
  const isManager = hasRole(['admin', 'store_manager']);
  
  // State
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showDeleted, setShowDeleted] = useState(false);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  
  // History dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  
  // Pending Actions dialog - P0.3
  const [pendingDialogOpen, setPendingDialogOpen] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [importDialog, setImportDialog] = useState({ open: false, file: null, upsert: true, importing: false });
  const [hardDeleteDialogOpen, setHardDeleteDialogOpen] = useState(false);
  const [hardDeleteConfirmation, setHardDeleteConfirmation] = useState('');
  const [hardDeleteReason, setHardDeleteReason] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    id: null,
    code: '',
    client_code: '',
    name: '',
    client_type: 'individual',
    nuit: '',
    email: '',
    phone: '',
    phone2: '',
    address: '',
    city: '',
    contact_person: '',
    notes: ''
  });
  
  // Load clients
  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await clientsAPI.list({ 
        include_deleted: showDeleted,
        client_type: filterType !== 'all' ? filterType : null
      });
      setClients(response.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar clientes'));
    } finally {
      setLoading(false);
    }
  }, [showDeleted, filterType]);
  
  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    setSelectedClientIds([]);
  }, [showDeleted, filterType, searchTerm]);
  
  // Filter clients by search term
  const filteredClients = useMemo(() => {
    if (!searchTerm) return clients;
    const term = searchTerm.toLowerCase();
    return clients.filter((client) => (
      client.name?.toLowerCase().includes(term) ||
      client.nuit?.toLowerCase().includes(term) ||
      (client.phone || '').includes(term) ||
      client.email?.toLowerCase().includes(term) ||
      client.code?.toLowerCase().includes(term) ||
      client.client_code?.toLowerCase().includes(term)
    ));
  }, [clients, searchTerm]);
  
  // Open create dialog
  const handleCreate = () => {
    setFormData({
      id: null,
      code: '',
      client_code: '',
      name: '',
      client_type: 'individual',
      nuit: '',
      email: '',
      phone: '',
      phone2: '',
      address: '',
      city: '',
      contact_person: '',
      notes: ''
    });
    setEditMode(false);
    setSelectedClient(null);
    setDialogOpen(true);
  };
  
  // Open edit dialog
  const handleEdit = (client) => {
    setFormData({
      id: client.id || null,
      code: client.code || '',
      client_code: client.client_code || client.code || '',
      name: client.name || '',
      client_type: client.client_type || 'individual',
      nuit: client.nuit || '',
      email: client.email || '',
      phone: client.phone || '',
      phone2: client.phone2 || '',
      address: client.address || '',
      city: client.city || '',
      contact_person: client.contact_person || '',
      notes: client.notes || ''
    });
    setEditMode(true);
    setSelectedClient(client);
    setDialogOpen(true);
  };
  
  // Save client
  const handleSave = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error('Nome é obrigatório');
        return;
      }

      if (editMode && !selectedClient?.id) {
        toast.error('Cliente inválido para edição');
        return;
      }
      
      if (editMode && selectedClient) {
        await clientsAPI.update(selectedClient.id, formData);
        toast.success('Cliente actualizado');
      } else {
        await clientsAPI.create(formData);
        toast.success('Cliente criado');
      }
      
      setDialogOpen(false);
      loadClients();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao guardar cliente'));
    }
  };
  
  // Open delete dialog
  const handleDirectAdminDelete = async (client) => {
    try {
      await clientsAPI.delete(client.id, 'Eliminação directa pelo administrador');
      toast.success('Cliente eliminado pelo administrador');
      loadClients();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao eliminar cliente'));
    }
  };

  const handleDeleteClick = (client) => {
    if (isAdmin) {
      handleDirectAdminDelete(client);
      return;
    }
    setSelectedClient(client);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };
  
  // Confirm delete
  const handleDelete = async () => {
    try {
      if (!deleteReason.trim() || deleteReason.trim().length < 5) {
        toast.error('Motivo obrigatório (mínimo 5 caracteres)');
        return;
      }

      if (!selectedClient?.id) {
        toast.error('Cliente inválido para eliminação');
        return;
      }
      
      await clientsAPI.delete(selectedClient.id, deleteReason);
      toast.success('Cliente eliminado');
      setDeleteDialogOpen(false);
      loadClients();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao eliminar cliente'));
    }
  };
  
  // Restore deleted client
  const handleRestore = async (client) => {
    try {
      await clientsAPI.restore(client.id);
      toast.success('Cliente restaurado');
      loadClients();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao restaurar cliente'));
    }
  };
  
  // View history
  const handleViewHistory = async (client) => {
    try {
      const response = await clientsAPI.getHistory(client.id);
      setHistoryData(response.data);
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar histórico'));
    }
  };
  
  // View pending actions - P0.3
  const handleViewPendingActions = async (client) => {
    try {
      setPendingLoading(true);
      setSelectedClient(client);
      setPendingDialogOpen(true);
      const response = await licensesAPI.getClientPendingActions(client.id);
      setPendingData(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar ações pendentes'));
      setPendingDialogOpen(false);
    } finally {
      setPendingLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await clientsAPI.downloadTemplate();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = 'clientes_import_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
      toast.success('Template de clientes descarregado');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao descarregar template'));
    }
  };

  const handleImportClients = async () => {
    if (!importDialog.file) {
      toast.error('Seleccione ficheiro .xlsx');
      return;
    }
    try {
      setImportDialog((prev) => ({ ...prev, importing: true }));
      const response = await clientsAPI.importExcel(importDialog.file, importDialog.upsert);
      toast.success(`Importação concluída: ${response.data.created} criados, ${response.data.updated} actualizados`);
      if (response.data.total_errors > 0) {
        toast.warning(`Erros detectados: ${response.data.total_errors}`);
      }
      setImportDialog({ open: false, file: null, upsert: true, importing: false });
      loadClients();
    } catch (error) {
      setImportDialog((prev) => ({ ...prev, importing: false }));
      toast.error(getErrorMessage(error, 'Erro na importação'));
    }
  };

  const toggleClientSelection = (clientId, checked) => {
    setSelectedClientIds((prev) => {
      if (checked) {
        return prev.includes(clientId) ? prev : [...prev, clientId];
      }
      return prev.filter((id) => id !== clientId);
    });
  };

  const allVisibleSelected = filteredClients.length > 0 && filteredClients.every((client) => selectedClientIds.includes(client.id));

  const toggleSelectAllVisible = (checked) => {
    if (checked) {
      setSelectedClientIds(filteredClients.map((client) => client.id));
      return;
    }
    setSelectedClientIds([]);
  };

  const handleBulkHardDelete = async () => {
    if (!selectedClientIds.length) {
      toast.error('Seleccione clientes para hard delete');
      return;
    }
    try {
      const response = await clientsAPI.hardDeleteBulk({
        ids: selectedClientIds,
        confirmation: hardDeleteConfirmation,
        reason: hardDeleteReason,
      });
      const blockedCount = (response.data.blocked || []).length;
      toast.success(`Hard delete concluído: ${response.data.deleted} removidos${blockedCount ? `, ${blockedCount} bloqueados` : ''}`);
      setHardDeleteDialogOpen(false);
      setHardDeleteConfirmation('');
      setHardDeleteReason('');
      setSelectedClientIds([]);
      loadClients();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro no hard delete em massa'));
    }
  };

  const handleBulkStatus = async (action) => {
    if (!selectedClientIds.length) {
      toast.error('Seleccione clientes para ação em massa');
      return;
    }
    try {
      const response = await clientsAPI.bulkStatus({
        ids: selectedClientIds,
        action,
        reason: `Ação em massa: ${action}`,
      });
      toast.success(response.data.message || 'Ação em massa concluída');
      setSelectedClientIds([]);
      loadClients();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro na ação em massa'));
    }
  };

  const handleExportSelectedClients = () => {
    if (!selectedClientIds.length) {
      toast.error('Seleccione clientes para exportar');
      return;
    }
    const selected = clients.filter((client) => selectedClientIds.includes(client.id));
    if (!selected.length) {
      toast.error('Nenhum cliente seleccionado para exportação');
      return;
    }

    const headers = ['Código', 'Nome', 'Tipo', 'NUIT', 'Telefone', 'Email', 'Cidade', 'Estado'];
    const lines = selected.map((client) => [
      client.client_code || client.code || '',
      client.name || '',
      client.client_type === 'company' ? 'Empresa' : 'Particular',
      client.nuit || '',
      client.phone || '',
      client.email || '',
      client.city || '',
      client.is_deleted ? 'Eliminado' : (client.is_active ? 'Activo' : 'Inactivo'),
    ]);

    const csvContent = [headers, ...lines]
      .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `clientes_seleccionados_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`${selected.length} clientes exportados`);
  };
  
  return (
    <div className="p-4 space-y-4" data-testid="clients-page">
      <ModulePageHero
        testId="clients-page-hero"
        title={t('clientsPage.title')}
        description={t('clientsPage.description')}
        icon={Users}
        badge="CRM"
        compact
        stats={[
          { label: t('clientsPage.listTitle', { count: filteredClients.length }), value: filteredClients.length, helper: 'visíveis no filtro actual' },
          { label: 'Empresas', value: clients.filter((client) => client.client_type === 'company').length, helper: 'perfil corporativo' },
          { label: 'Particulares', value: clients.filter((client) => client.client_type !== 'company').length, helper: 'perfil individual' },
          { label: 'Seleccionados', value: selectedClientIds.length, helper: 'prontos para acções em lote' },
        ]}
        actions={(
          <div className="flex flex-wrap gap-1.5 justify-end">
          <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-clients-template-btn">
            <Download className="h-4 w-4 mr-2" />
            {t('clientsPage.templateExcel')}
          </Button>
          <Button variant="outline" onClick={() => setImportDialog({ open: true, file: null, upsert: true, importing: false })} data-testid="open-clients-import-dialog-btn">
            <Upload className="h-4 w-4 mr-2" />
            {t('clientsPage.importExcel')}
          </Button>
          <Button
            variant="outline"
            disabled={!selectedClientIds.length}
            onClick={() => handleBulkStatus('deactivate')}
            data-testid="clients-bulk-deactivate-btn"
          >
            {t('clientsPage.deactivate')}
          </Button>
          <Button
            variant="outline"
            disabled={!selectedClientIds.length}
            onClick={() => handleBulkStatus('restore_soft')}
            data-testid="clients-bulk-restore-btn"
          >
            {t('actions.restore')}
          </Button>
          <Button
            variant="outline"
            disabled={!selectedClientIds.length}
            onClick={handleExportSelectedClients}
            data-testid="clients-bulk-export-btn"
          >
            {t('actions.export')}
          </Button>
          {isAdmin && (
            <Button
              variant="destructive"
              disabled={!selectedClientIds.length}
              onClick={() => setHardDeleteDialogOpen(true)}
              data-testid="open-clients-hard-delete-dialog-btn"
            >
              <CheckSquare className="h-4 w-4 mr-2" />
              Hard Delete ({selectedClientIds.length})
            </Button>
          )}
          <Button onClick={handleCreate} data-testid="create-client-btn">
            <Plus className="h-4 w-4 mr-2" />
            {t('clientsPage.newClient')}
          </Button>
          </div>
        )}
      />
      
      {/* Filters */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-3">
          <div className="flex flex-wrap gap-3 items-end rounded-2xl border border-slate-200 bg-slate-50/70 p-3">
            <div className="flex-1 min-w-[250px]">
              <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{t('actions.search')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('clientsPage.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-9 pl-10 text-sm"
                  data-testid="search-clients-input"
                />
              </div>
            </div>
            
            <div className="w-[180px]">
              <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{t('clientsPage.type')}</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="h-9" data-testid="filter-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('clientsPage.all')}</SelectItem>
                  <SelectItem value="individual">{t('clientsPage.individual')}</SelectItem>
                  <SelectItem value="company">{t('clientsPage.company')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {isAdmin && (
              <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
                <input
                  type="checkbox"
                  id="showDeleted"
                  checked={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}
                  className="h-4 w-4"
                />
                <Label htmlFor="showDeleted" className="cursor-pointer">
                  {t('clientsPage.showDeleted')}
                </Label>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Clients Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="px-3 py-2.5">
          <CardTitle className="text-sm font-semibold">{t('clientsPage.listTitle', { count: filteredClients.length })}</CardTitle>
        </CardHeader>
        <CardContent className="p-3 pt-0">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">{t('clientsPage.loading')}</div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('clientsPage.noClientsFound')}
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[560px]">
            <Table className="text-[11px] leading-tight">
              <TableHeader className="sticky top-0 z-10 bg-white">
                <TableRow>
                  <TableHead className="relative" style={getClientsColumnStyle('selector')}>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                      data-testid="clients-select-all-checkbox"
                    />
                    <ResizableTableHandle onMouseDown={(event) => startClientsColumnResize('selector', event)} testId="clients-column-resize-selector" />
                  </TableHead>
                  <TableHead className="relative" style={getClientsColumnStyle('code')}>Código<ResizableTableHandle onMouseDown={(event) => startClientsColumnResize('code', event)} testId="clients-column-resize-code" /></TableHead>
                  <TableHead className="relative" style={getClientsColumnStyle('name')}>Nome<ResizableTableHandle onMouseDown={(event) => startClientsColumnResize('name', event)} testId="clients-column-resize-name" /></TableHead>
                  <TableHead className="relative" style={getClientsColumnStyle('type')}>Tipo<ResizableTableHandle onMouseDown={(event) => startClientsColumnResize('type', event)} testId="clients-column-resize-type" /></TableHead>
                  <TableHead className="relative" style={getClientsColumnStyle('nuit')}>NUIT<ResizableTableHandle onMouseDown={(event) => startClientsColumnResize('nuit', event)} testId="clients-column-resize-nuit" /></TableHead>
                  <TableHead className="relative" style={getClientsColumnStyle('phone')}>Telefone<ResizableTableHandle onMouseDown={(event) => startClientsColumnResize('phone', event)} testId="clients-column-resize-phone" /></TableHead>
                  <TableHead className="relative" style={getClientsColumnStyle('email')}>Email<ResizableTableHandle onMouseDown={(event) => startClientsColumnResize('email', event)} testId="clients-column-resize-email" /></TableHead>
                  <TableHead className="relative" style={getClientsColumnStyle('city')}>Cidade<ResizableTableHandle onMouseDown={(event) => startClientsColumnResize('city', event)} testId="clients-column-resize-city" /></TableHead>
                  <TableHead className="relative text-right" style={getClientsColumnStyle('actions')}>Acções<ResizableTableHandle onMouseDown={(event) => startClientsColumnResize('actions', event)} testId="clients-column-resize-actions" /></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client) => (
                  <TableRow 
                    key={client.id}
                    className={cn(client.is_deleted && "opacity-50 bg-red-50")}
                    data-testid={`client-row-${client.id}`}
                  >
                    <TableCell style={getClientsColumnStyle('selector')}>
                      <input
                        type="checkbox"
                        checked={selectedClientIds.includes(client.id)}
                        onChange={(e) => toggleClientSelection(client.id, e.target.checked)}
                        data-testid={`client-select-checkbox-${client.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-sm" style={getClientsColumnStyle('code')}>
                      {client.client_code || client.code || 'N/A'}
                    </TableCell>
                    <TableCell className="font-medium" style={getClientsColumnStyle('name')}>
                      {client.name}
                      {client.is_deleted && (
                        <Badge variant="destructive" className="ml-2">Eliminado</Badge>
                      )}
                    </TableCell>
                    <TableCell style={getClientsColumnStyle('type')}>
                      <Badge variant={client.client_type === 'company' ? 'default' : 'secondary'}>
                        {client.client_type === 'company' ? 'Empresa' : 'Particular'}
                      </Badge>
                    </TableCell>
                    <TableCell style={getClientsColumnStyle('nuit')}>{client.nuit || '-'}</TableCell>
                    <TableCell style={getClientsColumnStyle('phone')}>{client.phone || '-'}</TableCell>
                    <TableCell style={getClientsColumnStyle('email')}>{client.email || '-'}</TableCell>
                    <TableCell style={getClientsColumnStyle('city')}>{client.city || '-'}</TableCell>
                    <TableCell className="text-right" style={getClientsColumnStyle('actions')}>
                      <UniversalCrudActions
                        entityLabel="client"
                        entityId={client.id}
                        isDeleted={client.is_deleted}
                        canView={false}
                        canEdit={!client.is_deleted}
                        canDelete={isManager && !client.is_deleted}
                        canRecover={isAdmin && client.is_deleted}
                        onEdit={() => handleEdit(client)}
                        onDelete={() => handleDeleteClick(client)}
                        onRecover={() => handleRestore(client)}
                        extraActions={
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleViewPendingActions(client)}
                              title="Ações Pendentes (Licenças/Garantias)"
                              data-testid={`view-pending-${client.id}`}
                              className="text-orange-600 hover:text-orange-700"
                            >
                              <AlertTriangle className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleViewHistory(client)}
                              title="Ver histórico"
                              data-testid={`view-history-${client.id}`}
                            >
                              <History className="h-4 w-4" />
                            </Button>
                          </>
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[min(96vw,860px)] max-w-[860px] max-h-[88vh] overflow-y-auto p-4 sm:p-5">
          <DialogHeader>
            <DialogTitle>
              {editMode ? 'Editar Cliente' : 'Novo Cliente'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {editMode && (
                <div className="col-span-2">
                  <Label>Código do Cliente</Label>
                  <Input
                    value={formData.client_code || formData.code || ''}
                    disabled
                    data-testid="input-client-code"
                  />
                </div>
              )}
              <div className="col-span-2">
                <Label>Nome *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Nome completo ou razão social"
                  data-testid="input-client-name"
                />
              </div>
              
              <div>
                <Label>Tipo</Label>
                <Select 
                  value={formData.client_type} 
                  onValueChange={(v) => setFormData({...formData, client_type: v})}
                >
                  <SelectTrigger data-testid="select-client-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLIENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>NUIT</Label>
                <Input
                  value={formData.nuit}
                  onChange={(e) => setFormData({...formData, nuit: e.target.value})}
                  placeholder="Número fiscal"
                  data-testid="input-client-nuit"
                />
              </div>
              
              <div>
                <Label>Telefone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+258 84 xxx xxxx"
                  data-testid="input-client-phone"
                />
              </div>
              
              <div>
                <Label>Telefone 2</Label>
                <Input
                  value={formData.phone2}
                  onChange={(e) => setFormData({...formData, phone2: e.target.value})}
                  placeholder="Alternativo"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="email@exemplo.com"
                  data-testid="input-client-email"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Endereço</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="Rua, número, bairro"
                  data-testid="input-client-address"
                />
              </div>
              
              <div>
                <Label>Cidade</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  placeholder="Cidade"
                  data-testid="input-client-city"
                />
              </div>
              
              <div className="col-span-2">
                <Label>
                  Pessoa de Contacto
                  {formData.client_type === 'company' && (
                    <span className="text-xs text-muted-foreground ml-1">(recomendado para empresas)</span>
                  )}
                </Label>
                <Input
                  value={formData.contact_person}
                  onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                  placeholder="Nome do responsável de contacto"
                  data-testid="input-client-contact-person"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Notas</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Observações sobre o cliente"
                  rows={2}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} data-testid="save-client-btn">
              {editMode ? 'Actualizar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Cliente</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja eliminar &quot;{selectedClient?.name}&quot;?
              Esta acção pode ser revertida por um administrador.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4">
            <Label>Motivo da eliminação *</Label>
            <Textarea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Explique o motivo (mínimo 5 caracteres)"
              rows={3}
              data-testid="delete-reason-input"
            />
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Histórico de {historyData?.client?.name}
            </DialogTitle>
          </DialogHeader>
          
          {historyData && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-sm text-muted-foreground">Total Vendas</div>
                    <div className="text-2xl font-bold">{historyData.sales_count}</div>
                    <div className="text-sm text-green-600">
                      {formatCurrency(historyData.total_sales)}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-sm text-muted-foreground">Total Serviços</div>
                    <div className="text-2xl font-bold">{historyData.services_count}</div>
                    <div className="text-sm text-blue-600">
                      {formatCurrency(historyData.total_services)}
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Recent sales */}
              {historyData.recent_sales?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Vendas Recentes</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {historyData.recent_sales.map(sale => (
                      <div key={sale.id} className="flex justify-between items-center text-sm p-2 bg-slate-50 rounded">
                        <span>{formatDisplayCode(sale.sale_number, 'SAL')}</span>
                        <span>{formatCurrency(sale.total_amount)}</span>
                        <Badge variant={sale.status === 'completed' ? 'default' : 'secondary'}>
                          {sale.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Recent services */}
              {historyData.recent_services?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Serviços Recentes</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {historyData.recent_services.map(svc => (
                      <div key={svc.id} className="flex justify-between items-center text-sm p-2 bg-slate-50 rounded">
                        <span>{formatDisplayCode(svc.service_number, svc.service_type === 'warranty_claim' ? 'WAR' : 'SRV')}</span>
                        <span>{formatCurrency(svc.total_cost)}</span>
                        <Badge variant={svc.status === 'completed' ? 'default' : 'secondary'}>
                          {svc.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Pending Actions Dialog - P0.3 */}
      <Dialog open={pendingDialogOpen} onOpenChange={setPendingDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Ações Pendentes - {selectedClient?.name}
            </DialogTitle>
            <DialogDescription>
              Garantias e licenças a expirar nos próximos 30 dias
            </DialogDescription>
          </DialogHeader>
          
          {pendingLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : pendingData ? (
            <div className="space-y-4">
              {/* Summary Card */}
              <div className="grid grid-cols-3 gap-3">
                <Card className={cn(
                  pendingData.breakdown?.critical > 0 && "border-red-300 bg-red-50"
                )}>
                  <CardContent className="pt-4 pb-3 text-center">
                    <p className="text-sm text-red-600 font-medium">Críticos (≤7 dias)</p>
                    <p className="text-2xl font-bold text-red-600">{pendingData.breakdown?.critical || 0}</p>
                  </CardContent>
                </Card>
                <Card className={cn(
                  pendingData.breakdown?.warning > 0 && "border-orange-300 bg-orange-50"
                )}>
                  <CardContent className="pt-4 pb-3 text-center">
                    <p className="text-sm text-orange-600 font-medium">Atenção (8-15 dias)</p>
                    <p className="text-2xl font-bold text-orange-600">{pendingData.breakdown?.warning || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 text-center">
                    <p className="text-sm text-blue-600 font-medium">Info (16-30 dias)</p>
                    <p className="text-2xl font-bold text-blue-600">{pendingData.breakdown?.info || 0}</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Actions List */}
              {pendingData.total_pending === 0 ? (
                <div className="text-center py-8">
                  <Shield className="h-12 w-12 text-green-600 mx-auto mb-3" />
                  <p className="text-lg font-medium text-green-600">Nenhuma acção pendente!</p>
                  <p className="text-sm text-muted-foreground">
                    Este cliente não tem garantias ou licenças a expirar nos próximos 30 dias.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingData.actions?.map((action, idx) => (
                    <Card 
                      key={`${action.type}-${action.id}-${idx}`}
                      className={cn(
                        "border-l-4",
                        action.urgency === 'critical' && "border-l-red-500 bg-red-50",
                        action.urgency === 'warning' && "border-l-orange-500 bg-orange-50",
                        action.urgency === 'info' && "border-l-blue-500 bg-blue-50"
                      )}
                    >
                      <CardContent className="pt-4 pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            {action.type === 'license_expiring' ? (
                              <Key className="h-5 w-5 text-primary mt-0.5" />
                            ) : (
                              <Shield className="h-5 w-5 text-green-600 mt-0.5" />
                            )}
                            <div>
                              <p className="font-medium">{action.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {action.type === 'license_expiring' 
                                  ? `Licença - ${action.category || ''} ${action.seat_count > 1 ? `(${action.seat_count} seats)` : ''}`
                                  : `Garantia - SN: ${action.serial_number || '-'}`
                                }
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <Clock className="h-3 w-3 text-muted-foreground" />
                                <span className="text-xs">
                                  Expira: {new Date(action.end_date).toLocaleDateString('pt-MZ', { timeZone: 'Africa/Maputo' })}
                                </span>
                                <Badge 
                                  variant="outline" 
                                  className={cn(
                                    "text-xs",
                                    action.urgency === 'critical' && "border-red-500 text-red-700 bg-red-100",
                                    action.urgency === 'warning' && "border-orange-500 text-orange-700 bg-orange-100",
                                    action.urgency === 'info' && "border-blue-500 text-blue-700 bg-blue-100"
                                  )}
                                >
                                  {action.days_remaining === 0 
                                    ? 'Hoje!' 
                                    : action.days_remaining < 0 
                                      ? `Expirou há ${Math.abs(action.days_remaining)} dias`
                                      : `${action.days_remaining} dias`
                                  }
                                </Badge>
                              </div>
                            </div>
                          </div>
                          
                          {/* Action buttons */}
                          <div className="flex gap-1">
                            {action.renewable && (
                              <Button variant="outline" size="sm" title="Criar Renovação">
                                <Repeat className="h-3 w-3 mr-1" />
                                Renovar
                              </Button>
                            )}
                            {selectedClient?.phone && (
                              <WhatsAppButton
                                phone={selectedClient.phone}
                                clientName={selectedClient.name}
                                templateType={action.type}
                                variables={{
                                  license_name: action.name,
                                  warranty_item: action.name,
                                  expiration_date: new Date(action.end_date).toLocaleDateString('pt-MZ', { timeZone: 'Africa/Maputo' }),
                                  days_remaining: String(action.days_remaining || 0)
                                }}
                                variant="outline"
                                size="sm"
                              />
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              
              {/* Quick Actions Footer */}
              {pendingData.total_pending > 0 && (
                <div className="flex justify-between items-center pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Total: {pendingData.licenses_expiring || 0} licenças, {pendingData.warranties_expiring || 0} garantias
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm">
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Criar Venda de Renovação
                    </Button>
                    <Button variant="outline" size="sm">
                      <Wrench className="h-4 w-4 mr-2" />
                      Criar Serviço
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Clients Dialog */}
      <Dialog
        open={importDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setImportDialog({ open: false, file: null, upsert: true, importing: false });
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Importar Clientes por Excel</DialogTitle>
            <DialogDescription>
              Use o template oficial para cadastro/actualização em massa.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Ficheiro .xlsx</Label>
              <Input
                type="file"
                accept=".xlsx"
                onChange={(e) => setImportDialog((prev) => ({ ...prev, file: e.target.files?.[0] || null }))}
                data-testid="clients-import-file-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="clients-import-upsert-checkbox"
                type="checkbox"
                checked={importDialog.upsert}
                onChange={(e) => setImportDialog((prev) => ({ ...prev, upsert: e.target.checked }))}
                data-testid="clients-import-upsert-checkbox"
              />
              <Label htmlFor="clients-import-upsert-checkbox">Actualizar por NUIT quando já existir</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialog({ open: false, file: null, upsert: true, importing: false })}>
              Cancelar
            </Button>
            <Button onClick={handleImportClients} disabled={importDialog.importing} data-testid="clients-import-confirm-button">
              {importDialog.importing ? 'A importar...' : 'Importar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hard Delete Bulk Dialog */}
      <AlertDialog open={hardDeleteDialogOpen} onOpenChange={setHardDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hard Delete em Massa (Clientes)</AlertDialogTitle>
            <AlertDialogDescription>
              Acção irreversível. Apenas execute quando tiver certeza absoluta.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 py-2">
            <p className="text-sm">Seleccionados: <strong>{selectedClientIds.length}</strong></p>
            <div>
              <Label>Confirmação *</Label>
              <Input
                value={hardDeleteConfirmation}
                onChange={(e) => setHardDeleteConfirmation(e.target.value)}
                placeholder="Digite: APAGAR DEFINITIVAMENTE"
                data-testid="clients-hard-delete-confirmation-input"
              />
            </div>
            <div>
              <Label>Motivo *</Label>
              <Textarea
                value={hardDeleteReason}
                onChange={(e) => setHardDeleteReason(e.target.value)}
                placeholder="Motivo da remoção permanente"
                rows={3}
                data-testid="clients-hard-delete-reason-input"
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkHardDelete}
              className="bg-red-700 hover:bg-red-800"
              data-testid="clients-hard-delete-confirm-button"
            >
              Hard Delete Definitivo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
